import ReplayIcon from "@mui/icons-material/Replay";
import { Button, Tooltip } from "@mui/material";
import type { Delivery } from "@types_def/models/delivery.types";
import { DeliveryStatus } from "@types_def/models/delivery.types";
import { UserRank } from "@types_def/models/user.type";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useDataTableSelectionBarContext } from "@/components/ui/data-table-selection-bar";
import { useUpdateDeliveriesStatus } from "@/features/delivery/hooks/use-delivery-actions";

import { BulkSelectionConfirmDialog } from "./bulk-selection-confirm-dialog";
import { useBulkSelectionActionVisibility } from "./use-bulk-selection-action-visibility";

export function BulkActivateAction() {
  const visible = useBulkSelectionActionVisibility({
    requireAdmin: false,
    minRank: UserRank.MAINTAINER,
  });
  const { selectedData, table } = useDataTableSelectionBarContext<Delivery>();
  const { mutateAsync, isPending } = useUpdateDeliveriesStatus();
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!visible) {
    return null;
  }

  const count = selectedData.length;

  return (
    <>
      <Tooltip title={t("delivery.action.activate.title")}>
        <span>
          <Button
            color="success"
            disabled={count === 0 || isPending}
            onClick={() => setConfirmOpen(true)}
            size="small"
            startIcon={<ReplayIcon fontSize="small" />}
            variant="contained"
          >
            {t("delivery.action.activate.text")}
          </Button>
        </span>
      </Tooltip>

      <BulkSelectionConfirmDialog
        alertSeverity="info"
        confirmColor="success"
        confirmLabel={t("delivery.action.activate.text")}
        description={t("delivery.table.bulkConfirm.activate.description", { count })}
        isPending={isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          await mutateAsync({
            UpdateDeliveriesStatusInput: {
              ids: selectedData.map((r) => r.id),
              status: DeliveryStatus.REQUESTED,
            },
          });
          table.resetRowSelection();
          setConfirmOpen(false);
        }}
        open={confirmOpen}
        title={t("delivery.table.bulkConfirm.activate.title")}
      />
    </>
  );
}
